import { workspaceFetch } from '../../../../shared/platform/business-workspaces';
/** Private LOCAL SYN assessment seam. No certification or operational authority. */
export interface AssessmentAuth {
  session: string;
  csrf: string;
}
export interface Definition {
  capabilityId: string;
  displayName: string;
  capabilityType: string;
  definitionVersion: number;
  definitionDigest: string;
  definitionStatus: string;
  originLabel: string;
}
export interface AssetSnapshot {
  assetId: string;
  country: string;
  agency: string;
  publishedVersion: number;
  digest: string;
  displayName: string;
  configurationDeclaration: string;
  purpose: string;
  sourceNote: string;
  contentJson: string;
  submissionRevision: number;
}
export interface ProductSnapshot {
  productId: string;
  publishedVersion: number;
  digest: string;
  modelName: string;
  manufacturerClaims: string;
  source: string;
}
export interface Evidence {
  id: string;
  sha256: string;
  size: number;
  mime: string;
  name: string;
  source: string;
  basis: string;
  owner: string;
  country: string;
  scope: string;
  synthetic: true;
}
export const assessmentOutcomes = [
  'SYN_ASSESSED_SUPPORTED',
  'SYN_ASSESSED_UNSUPPORTED',
  'SYN_INCONCLUSIVE',
  'SYN_RETURNED_FOR_EVIDENCE',
] as const;
export type AssessmentOutcome = (typeof assessmentOutcomes)[number];
export const applicabilityStatuses = [
  'CURRENT',
  'STALE',
  'DEFINITION_UNKNOWN',
  'OWNER_UNAVAILABLE',
  'EVIDENCE_UNREADABLE',
  'EVIDENCE_HASH_MISMATCH',
  'SCOPE_DENIED',
] as const;
export interface Assessment {
  assessmentId: string;
  revision: number;
  digest: string;
  previousAssessmentId: string | null;
  asset: AssetSnapshot;
  product: ProductSnapshot | null;
  definition: Definition;
  evidence: Evidence[];
  method: string;
  observations: string;
  limitations: string;
  submitterAccount: string;
  submitterPerson: string;
  submittedAt: string;
  decision: {
    decisionId: string;
    outcome: AssessmentOutcome;
    reason: string;
    assessorAccount: string;
    assessorPerson: string;
    decidedAt: string;
  } | null;
  applicability: { status: (typeof applicabilityStatuses)[number]; reason: string };
}
export interface Workspace {
  asset: AssetSnapshot;
  product: ProductSnapshot | null;
  evidence: Evidence[];
  definitions: Definition[];
  assessments: Assessment[];
}
export interface SubmitAssessment {
  assetId: string;
  publishedVersion: number;
  assetDigest: string;
  capabilityId: string;
  definitionVersion: number;
  definitionDigest: string;
  evidence: { id: string; sha256: string }[];
  method: string;
  observations: string;
  limitations: string;
  previousAssessmentId?: string;
}
export interface ExactAsset {
  assetId: string;
  publishedVersion: number;
  digest: string;
}

function object(value: unknown): Record<string, unknown> {
  if (!value || typeof value !== 'object' || Array.isArray(value))
    throw new Error('CONTRACT_DEFECT：响应对象不可读取');
  return value as Record<string, unknown>;
}
function strings(value: unknown, keys: string[], allowEmpty: string[] = []) {
  const data = object(value);
  for (const key of keys) {
    if (
      typeof data[key] !== 'string' ||
      (!allowEmpty.includes(key) && !(data[key] as string).trim())
    )
      throw new Error(`CONTRACT_DEFECT：${key}`);
  }
  return data;
}
function positive(value: unknown) {
  if (!Number.isSafeInteger(value) || (value as number) < 1)
    throw new Error('CONTRACT_DEFECT：版本或修订号');
}
function hash(value: unknown) {
  if (typeof value !== 'string' || !/^[a-f0-9]{64}$/.test(value))
    throw new Error('CONTRACT_DEFECT：SHA-256');
}
function list<T>(value: unknown, parse: (item: unknown) => T): T[] {
  if (!Array.isArray(value)) throw new Error('CONTRACT_DEFECT：列表不可读取');
  return value.map(parse);
}
export function parseDefinition(value: unknown): Definition {
  const data = strings(value, [
    'capabilityId',
    'displayName',
    'capabilityType',
    'definitionDigest',
    'definitionStatus',
    'originLabel',
  ]);
  positive(data.definitionVersion);
  // CapabilityDefinitionSnapshotV1 defines a nonblank opaque reference, not a
  // SHA-256 value. Preserve it verbatim for exact version/digest comparisons.
  return data as unknown as Definition;
}
function parseAsset(value: unknown): AssetSnapshot {
  const data = strings(
    value,
    [
      'assetId',
      'country',
      'agency',
      'digest',
      'displayName',
      'configurationDeclaration',
      'purpose',
      'sourceNote',
      'contentJson',
    ],
    ['contentJson'],
  );
  positive(data.publishedVersion);
  positive(data.submissionRevision);
  hash(data.digest);
  return data as unknown as AssetSnapshot;
}
function parseProduct(value: unknown): ProductSnapshot | null {
  if (value === null) return null;
  const data = strings(
    value,
    ['productId', 'digest', 'modelName', 'manufacturerClaims', 'source'],
    ['manufacturerClaims'],
  );
  positive(data.publishedVersion);
  hash(data.digest);
  return data as unknown as ProductSnapshot;
}
export function parseEvidence(value: unknown): Evidence {
  const data = strings(value, [
    'id',
    'sha256',
    'mime',
    'name',
    'source',
    'basis',
    'owner',
    'country',
    'scope',
  ]);
  hash(data.sha256);
  positive(data.size);
  if (
    data.synthetic !== true ||
    data.owner !== '05' ||
    !['image/png', 'image/jpeg', 'application/pdf'].includes(data.mime as string)
  )
    throw new Error('CONTRACT_DEFECT：非受控合成材料');
  return data as unknown as Evidence;
}
export function parseAssessment(value: unknown): Assessment {
  const data = strings(value, [
    'assessmentId',
    'digest',
    'method',
    'observations',
    'limitations',
    'submitterAccount',
    'submitterPerson',
    'submittedAt',
  ]);
  positive(data.revision);
  hash(data.digest);
  if (
    data.previousAssessmentId !== null &&
    (typeof data.previousAssessmentId !== 'string' || !data.previousAssessmentId)
  )
    throw new Error('CONTRACT_DEFECT：前序评估');
  parseAsset(data.asset);
  parseProduct(data.product);
  parseDefinition(data.definition);
  const evidence = list(data.evidence, parseEvidence);
  if (!evidence.length || new Set(evidence.map((e) => e.id)).size !== evidence.length)
    throw new Error('CONTRACT_DEFECT：证据缺失或重复');
  const asset = data.asset as AssetSnapshot;
  if (evidence.some((e) => e.country !== asset.country || e.scope !== asset.agency))
    throw new Error('CONTRACT_DEFECT：证据范围不匹配');
  const applicability = strings(data.applicability, ['status', 'reason'], ['reason']);
  if (
    !applicabilityStatuses.includes(applicability.status as (typeof applicabilityStatuses)[number])
  )
    throw new Error('CONTRACT_DEFECT：适用性状态');
  if (!Number.isFinite(Date.parse(data.submittedAt as string)))
    throw new Error('CONTRACT_DEFECT：提交时间');
  if (data.decision !== null) {
    const decision = strings(data.decision, [
      'decisionId',
      'outcome',
      'reason',
      'assessorAccount',
      'assessorPerson',
      'decidedAt',
    ]);
    if (
      !assessmentOutcomes.includes(decision.outcome as AssessmentOutcome) ||
      !Number.isFinite(Date.parse(decision.decidedAt as string)) ||
      decision.assessorPerson === data.submitterPerson
    )
      throw new Error('CONTRACT_DEFECT：独立评估决定');
  }
  return data as unknown as Assessment;
}
export function parseWorkspace(value: unknown, exact: ExactAsset): Workspace {
  const data = object(value),
    asset = parseAsset(data.asset);
  if (
    asset.assetId !== exact.assetId ||
    asset.publishedVersion !== exact.publishedVersion ||
    asset.digest !== exact.digest
  )
    throw new Error('CONTRACT_DEFECT：装备精确版本不匹配');
  const assessments = list(data.assessments, parseAssessment),
    evidence = list(data.evidence, parseEvidence);
  if (
    assessments.some(
      (a) =>
        a.asset.assetId !== asset.assetId ||
        a.asset.country !== asset.country ||
        a.asset.agency !== asset.agency,
    ) ||
    evidence.some((e) => e.country !== asset.country || e.scope !== asset.agency)
  )
    throw new Error('CONTRACT_DEFECT：范围不匹配');
  return {
    asset,
    product: parseProduct(data.product),
    definitions: list(data.definitions, parseDefinition),
    evidence,
    assessments,
  };
}

/** Stable key for a failed action's exact payload; a confirmed response ends that action. */
export function createActionKeys(newKey: () => string = () => crypto.randomUUID()) {
  const pending = new Map<string, string>();
  return {
    get(action: string, body: unknown) {
      const fingerprint = JSON.stringify([action, body]);
      let key = pending.get(fingerprint);
      if (!key) {
        key = `syn-assessment-${newKey()}`;
        pending.set(fingerprint, key);
      }
      return key;
    },
    confirmed(action: string, body: unknown) {
      pending.delete(JSON.stringify([action, body]));
    },
  };
}

export function createSynAssessmentAdapter(
  baseOrigin: string,
  auth: AssessmentAuth,
  send: typeof fetch = workspaceFetch,
) {
  if (!/^https?:\/\/[^/?#\\\s@]+\/?$/.test(baseOrigin)) throw new Error('SYN评估只允许本机根地址');
  const origin = new URL(baseOrigin);
  if (
    !['localhost', '127.0.0.1', '[::1]'].includes(origin.hostname) ||
    origin.username ||
    origin.password
  )
    throw new Error('SYN评估只允许本机根地址');
  const root = `${origin.origin}/management/local-asset/syn-assessments`,
    keys = createActionKeys();
  const headers = { 'X-Rescue-Synthetic-Session': auth.session, 'X-Rescue-CSRF': auth.csrf };
  async function request(path: string, body?: unknown, signal?: AbortSignal): Promise<Response> {
    if (!auth.session) throw new Error('SESSION_REQUIRED：请先建立现有合成会话');
    const init: RequestInit = {
      method: body === undefined ? 'GET' : 'POST',
      headers,
      credentials: 'omit',
      redirect: 'error',
      cache: 'no-store',
    };
    if (signal) init.signal = signal;
    if (body !== undefined) {
      init.body = JSON.stringify(body);
      init.headers = {
        ...headers,
        'Content-Type': 'application/json',
        'Idempotency-Key': keys.get(path, body),
      };
    }
    let response: Response;
    try {
      response = await send(`${root}${path}`, init);
    } catch (error) {
      if (signal?.aborted) throw error;
      throw new Error('OWNER_UNAVAILABLE：本次结果未确认；可用原内容与原幂等键重试。', {
        cause: error,
      });
    }
    if (!response.ok) {
      let code = 'UNREADABLE_ERROR';
      try {
        const result = object(await response.json());
        if (typeof result.error === 'string') code = result.error;
      } catch {
        /* Never parse an error as success. */
      }
      throw new Error(`${code}（HTTP ${response.status}）：服务端未确认本次操作。`);
    }
    return response;
  }
  async function json(path: string, body?: unknown, signal?: AbortSignal) {
    try {
      return (await (await request(path, body, signal)).json()) as unknown;
    } catch (error) {
      if (error instanceof SyntaxError)
        throw new Error('CONTRACT_DEFECT：响应无法读取，未确认结果。', { cause: error });
      throw error;
    }
  }
  const exactResult = (value: unknown, id: string) => {
    const record = parseAssessment(object(value).assessment);
    if (record.assessmentId !== id) throw new Error('CONTRACT_DEFECT：评估身份不匹配');
    return record;
  };
  return {
    async workspace(exact: ExactAsset, signal?: AbortSignal) {
      return parseWorkspace(
        await json(
          `/workspace/${encodeURIComponent(exact.assetId)}?${new URLSearchParams({ publishedVersion: String(exact.publishedVersion), digest: exact.digest })}`,
          undefined,
          signal,
        ),
        exact,
      );
    },
    async list(signal?: AbortSignal) {
      return list(object(await json('', undefined, signal)).assessments, parseAssessment);
    },
    async read(id: string, signal?: AbortSignal) {
      return exactResult(await json(`/${encodeURIComponent(id)}`, undefined, signal), id);
    },
    async submit(body: SubmitAssessment) {
      const record = parseAssessment(object(await json('', body)).assessment);
      if (
        record.asset.assetId !== body.assetId ||
        record.asset.publishedVersion !== body.publishedVersion ||
        record.asset.digest !== body.assetDigest ||
        record.definition.capabilityId !== body.capabilityId ||
        record.definition.definitionVersion !== body.definitionVersion ||
        record.definition.definitionDigest !== body.definitionDigest ||
        record.method !== body.method ||
        record.observations !== body.observations ||
        record.limitations !== body.limitations ||
        record.previousAssessmentId !== (body.previousAssessmentId ?? null) ||
        record.evidence.length !== body.evidence.length ||
        body.evidence.some(
          (e) => !record.evidence.some((r) => r.id === e.id && r.sha256 === e.sha256),
        )
      )
        throw new Error('CONTRACT_DEFECT：提交读回不匹配');
      keys.confirmed('', body);
      return record;
    },
    async decide(
      id: string,
      body: { expectedDigest: string; outcome: AssessmentOutcome; reason: string },
    ) {
      const path = `/${encodeURIComponent(id)}/decisions`,
        record = exactResult(await json(path, body), id);
      if (
        record.digest !== body.expectedDigest ||
        record.decision?.outcome !== body.outcome ||
        record.decision.reason !== body.reason
      )
        throw new Error('CONTRACT_DEFECT：决定读回不匹配');
      keys.confirmed(path, body);
      return record;
    },
    async evidence(assessmentId: string, evidence: Evidence, signal?: AbortSignal) {
      const response = await request(
        `/${encodeURIComponent(assessmentId)}/evidence/${encodeURIComponent(evidence.id)}`,
        undefined,
        signal,
      );
      const bytes = await response.arrayBuffer();
      const digest = Array.from(
        new Uint8Array(await crypto.subtle.digest('SHA-256', bytes)),
        (byte) => byte.toString(16).padStart(2, '0'),
      ).join('');
      if (digest !== evidence.sha256 || bytes.byteLength !== evidence.size)
        throw new Error('EVIDENCE_HASH_MISMATCH：材料摘要或长度不符，不能展示或下载。');
      return new Blob([bytes], { type: evidence.mime });
    },
  };
}
export type SynAssessmentAdapter = ReturnType<typeof createSynAssessmentAdapter>;
